import useLocalStorage from '@rehooks/local-storage'
import { Theme, useCss } from 'bold-ui'
import { useAcessoLotacao } from 'components/auth/useAcessoLotacao'
import useSession from 'components/auth/useSession'
import { Breadcrumb } from 'components/breadcrumb'
import { PageLoading } from 'components/loading'
import { PecSwitch } from 'components/route'
import { useAgendamentosDiaQuery, useAtendimentoIndividualViewQuery } from 'graphql/hooks.generated'
import { AtendimentoIndividualViewQuery, SituacaoAgendado, TipoAtendimentoProfissional } from 'graphql/types.generated'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { Redirect, Route, useRouteMatch } from 'react-router'
import { ProcedimentosAutomaticoAtendimentoOdonto, ProcedimentosAutomaticosAtendimentoIndividual } from 'types/enums'
import { isCboMedico } from 'util/atendimento/cboMedicoOuCirurgiaoDentista'

import Permissions from '../../../types/Permissions'
import { AtendimentoAntecedentesView } from '../detail/antecedentes/AtendimentoAntecedentesView'
import { AtendimentoHeader } from '../detail/AtendimentoHeader'
import { CidadaoDetailView } from '../detail/cadastro-cidadao/CidadaoDetailView'
import { FolhaRostoView } from '../detail/folha-rosto/FolhaRostoView'
import { HistoricoAtendimentoView } from '../detail/historico/HistoricoAtendimentoView'
import { SoapView } from '../detail/soap/SoapView'
import { initialValue, SoapState } from './model'

export type ProcedimentosAutomaticos = AtendimentoIndividualViewQuery['procedimentosAutomaticos']
export type ProcedimentoAutomatico = ProcedimentosAutomaticos[number]

interface AtendimentoIndividualViewProps {
  atendimentoId: ID
}

export function AtendimentoIndividualView(props: AtendimentoIndividualViewProps) {
  const { atendimentoId } = props
  const { data: sessao, hasAuthorization, isUnidadeSaudeCEO } = useSession()
  const { unidadeSaude } = useAcessoLotacao()
  const match = useRouteMatch()

  const {
    data: { atendimento, procedimentosAutomaticos },
    loading: loadingAtendimento,
  } = useAtendimentoIndividualViewQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      atendimentoId,
    },
  })

  const { agendado, cidadao, tiposServico, atendimentoProfissional, prontuario } = atendimento || {}

  const { data: agendamentosDiaQueryResults, loading: loadingAgendamento } = useAgendamentosDiaQuery({
    variables: {
      input: {
        cidadao: cidadao?.id,
        dataAgendado: moment().startOf('day').toDate(),
        situacao: [SituacaoAgendado.AGENDADO, SituacaoAgendado.CIDADAO_PRESENTE_NA_UNIDADE],
        unidadeSaude: unidadeSaude.id,
        foraDaUbs: false,
        origemAtencaoDomiciliar: false,
      },
    },
  })
  const agendamentosDia = agendamentosDiaQueryResults?.agendados?.content.filter(
    (agendado) => agendado.id !== atendimento.agendado?.id || agendado?.lotacaoAgendada?.id !== sessao.acesso.id
  )
  const isDemandaEspontanea = agendado?.lotacaoAgendada?.id !== sessao.acesso.id
  const isMedico = isCboMedico(sessao.acesso.__typename === 'Lotacao' && sessao.acesso.cbo.cbo2002)
  const isAtendimentoOdonto = atendimentoProfissional?.tipo === TipoAtendimentoProfissional.ATENDIMENTO_ODONTOLOGICO

  const isLoading = loadingAtendimento || loadingAgendamento
  const breadcrumbTitle = isAtendimentoOdonto ? 'Atendimento odontológico' : 'Atendimento individual'

  const [cacheState, setCacheState, deleteCacheState] = useLocalStorage<SoapState>(
    `${sessao.acesso.id}/atendimento/individual/${atendimentoId}`
  )
  const [initialStateLoaded, setInitialStateLoaded] = useState(!!cacheState)

  const canAccess = hasAuthorization(Permissions.visualizarListaDeAtendimento.cadastrarEditarEExcluir)

  useEffect(() => {
    const agendadoSelecionado = cacheState?.desfecho?.desfechoAtendimento?.novoAtendimento?.agendados?.[0]
    if (agendadoSelecionado && !agendamentosDia?.find((agendamentos) => agendamentos.id === agendadoSelecionado)) {
      cacheState.desfecho.desfechoAtendimento.novoAtendimento.agendados = []
    }

    if (!initialStateLoaded && !isLoading) {
      const initialProced = isMedico
        ? ProcedimentosAutomaticosAtendimentoIndividual.CONSULTA_MEDICA_EM_ATENCAO_BASICA
        : isUnidadeSaudeCEO
        ? ProcedimentosAutomaticoAtendimentoOdonto.CONSULTA_DE_PROFISSIONAIS_DE_NIVEL_SUPERIOR_NA_ATENCAO_ESPECIALIZADA
        : ProcedimentosAutomaticosAtendimentoIndividual.CONSULTA_DE_PROFISSIONAIS_DE_NIVEL_SUPERIOR_NA_ATENCAO_BASICA
      const proced = procedimentosAutomaticos?.find((proced) => proced.codigo === initialProced)

      setCacheState(initialValue(proced, isDemandaEspontanea, agendamentosDia, tiposServico, canAccess))
      setInitialStateLoaded(true)
    }
  }, [
    agendamentosDia,
    cacheState,
    canAccess,
    initialStateLoaded,
    isDemandaEspontanea,
    isLoading,
    isMedico,
    isUnidadeSaudeCEO,
    procedimentosAutomaticos,
    setCacheState,
    tiposServico,
  ])

  if (isLoading || !initialStateLoaded) {
    return (
      <>
        {atendimentoProfissional?.tipo && <Breadcrumb title={breadcrumbTitle} />}
        <PageLoading message='Carregando dados do atendimento individual...' />
      </>
    )
  }

  const renderFolhaRostoView = () => (
    <FolhaRostoView
      atendimentoId={atendimentoId}
      isAtendimentoOdonto={isAtendimentoOdonto}
      clearCache={deleteCacheState}
    />
  )

  const renderSoapView = () => (
    <SoapView
      atendimentoId={atendimentoId}
      prontuarioId={prontuario?.id}
      cidadao={cidadao}
      dataAtendimento={atendimento?.iniciadoEm}
      procedimentosAutomaticos={procedimentosAutomaticos || []}
      isMedico={isMedico}
      isDemandaEspontanea={isDemandaEspontanea}
      updateCache={setCacheState}
      cacheState={cacheState}
      clearCache={deleteCacheState}
      tiposServico={tiposServico}
      agendamento={agendado}
      agendamentosDia={agendamentosDia}
      isAtendimentoOdonto={isAtendimentoOdonto}
      tipoAtendProf={atendimentoProfissional.tipo}
      atendimentoProfissional={atendimentoProfissional}
    />
  )

  const renderAtendimentoHistoricoView = () => (
    <HistoricoAtendimentoView
      atendimentoId={atendimentoId}
      cidadaoId={cidadao.id}
      isAtendimentoOdonto={isAtendimentoOdonto}
      clearCache={deleteCacheState}
    />
  )

  const renderAtendimentoAntecedentesView = () => (
    <AtendimentoAntecedentesView
      atendimentoId={atendimentoId}
      isAtendimentoOdonto={isAtendimentoOdonto}
      clearCache={deleteCacheState}
    />
  )

  const renderCidadaoDetail = () => (
    <CidadaoDetailView
      atendimentoId={atendimentoId}
      cidadaoId={cidadao?.id}
      isAtendimentoOdonto={isAtendimentoOdonto}
      clearCache={deleteCacheState}
    />
  )

  return (
    <>
      {atendimentoProfissional?.tipo && <Breadcrumb title={breadcrumbTitle} />}
      <AtendimentoHeader cidadaoId={cidadao?.id} />
      <PecSwitch>
        <Redirect exact path={match.url} to={`${match.url}/folha-rosto`} />
        <Route path={`${match.url}/folha-rosto`} render={renderFolhaRostoView} />
        <Route path={`${match.url}/soap`} render={renderSoapView} />
        <Route path={`${match.url}/historico`} render={renderAtendimentoHistoricoView} />
        <Route path={`${match.url}/antecedentes`} render={renderAtendimentoAntecedentesView} />
        <Route path={`${match.url}/cadastro-cidadao`} render={renderCidadaoDetail} />
      </PecSwitch>
      {/* TODO
       * Necessário ajustar o botão "Finalizar atendimento individual",
       * para utilizar o componente aqui.
       * Enquanto isso, é colocado em cada aba.
       * <AtendimentoFooter atendimentoId={atendimentoId} clearCache={deleteCacheState} />
       * */}
    </>
  )
}

// copiado do bold pra memoizar o retorno, mas deve ser corrigido lá depois
export type StyleFactory<Classes extends string> = (
  theme: Theme,
  ...args: any[]
) => { readonly [key in Classes]: React.CSSProperties }

export type ClassNames<Classes extends string> = { readonly [key in Classes]: string }

const emptyStylesFactory: StyleFactory<any> = () => ({})

export const useLocalStyles = <Classes extends string>(
  factory: StyleFactory<Classes> = emptyStylesFactory,
  ...args: any[]
) => {
  const { css, theme } = useCss()

  return useMemo(() => {
    const map = factory(theme, ...args)

    const classes = Object.keys(map).reduce(
      (all, className) => ({
        ...all,
        [className]: css(map[className]),
      }),
      {} as ClassNames<Classes>
    )

    return { classes, css, theme }
  }, [args, css, factory, theme])
}
